import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './ChatPanel.css';
const MAX_MESSAGE_LENGTH = 500;
const ChatPanel = ({ isOpen: chatOpen, onClose, socket, currentUsername, onUsernameSet, isDarkMode, onThemeChange }) => {
    const { user, isAuthenticated } = useAuth();
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [username, setUsername] = useState('');
    const messagesEndRef = useRef(null);
    const hasSetAuthUsername = useRef(false);
    // Auto-set username from authenticated user
    useEffect(() => {
        if (isAuthenticated && user && socket && !currentUsername && !hasSetAuthUsername.current) {
            const authUsername = user.displayName || user.username;
            console.log('ðŸ” Auto-setting username from authenticated user:', authUsername);
            hasSetAuthUsername.current = true;
            onUsernameSet(authUsername);
            // Send username to server via chat command
            socket.emit('chat-command', {
                command: 'username',
                args: [authUsername],
                username: authUsername,
                socketId: socket.id
            });
        }
    }, [isAuthenticated, user, socket, currentUsername, onUsernameSet]);
    // Reset flag when user logs out
    useEffect(() => {
        if (!isAuthenticated) {
            hasSetAuthUsername.current = false;
        }
    }, [isAuthenticated]);
    // Clear login/register prompt and welcome message after authentication
    useEffect(() => {
        if (isAuthenticated) {
            setMessages(prev => prev.filter(msg => {
                // Remove login/register prompts
                if (msg.type === 'system' && (msg.message.includes('/login') || msg.message.includes('/register'))) {
                    return false;
                }
                // Remove welcome messages (Good morning, Good afternoon, Good evening)
                if (msg.type === 'system' && msg.username === '' &&
                    (msg.message.startsWith('Good morning') ||
                        msg.message.startsWith('Good afternoon') ||
                        msg.message.startsWith('Good evening'))) {
                    return false;
                }
                return true;
            }));
        }
    }, [isAuthenticated]);
    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    // Socket message listeners
    useEffect(() => {
        if (!socket) {
            console.log('ðŸ”¡ Socket not available for ChatPanel');
            return;
        }
        console.log('ðŸ”¡ Setting up socket listeners for ChatPanel');
        const handleChatMessage = (data) => {
            console.log('ðŸ’¬ Chat message received:', data);
            const newMessage = {
                username: data.username || '',
                message: data.message || '',
                timestamp: data.timestamp || Date.now(),
                socketId: data.socketId || '',
                type: data.type || 'normal',
                hasAdminLink: data.hasAdminLink || false
            };
            setMessages(prev => {
                const updated = [...prev, newMessage];
                return updated.slice(-100); // Keep only last 100 messages
            });
        };
        const handleChatHistory = (history) => {
            console.log('ðŸ“š Chat history received:', history.length, 'messages');
            setMessages(history || []);
        };
        const handleShowWelcome = (data) => {
            console.log('ðŸ‘‹ Show welcome message:', data);
            // Get time-based greeting using client's local time
            const now = new Date();
            const hour = now.getHours();
            let greeting = 'Good evening';
            if (hour >= 5 && hour < 12) {
                greeting = 'Good morning';
            }
            else if (hour >= 12 && hour < 17) {
                greeting = 'Good afternoon';
            }
            let message = '';
            if (data.isAuthenticated) {
                // Authenticated user - just the greeting with username
                message = `${greeting}, ${data.username}`;
            }
            else {
                // Anonymous user - just the greeting (login prompt is in placeholder only)
                message = `${greeting}`;
            }
            const welcomeMessage = {
                username: '',
                message: message,
                timestamp: Date.now(),
                socketId: '',
                type: 'system'
            };
            setMessages(prev => [...prev, welcomeMessage]);
        };
        socket.on('chat-message', handleChatMessage);
        socket.on('chat-history', handleChatHistory);
        socket.on('show-welcome-message', handleShowWelcome);
        return () => {
            socket.off('chat-message', handleChatMessage);
            socket.off('chat-history', handleChatHistory);
            socket.off('show-welcome-message', handleShowWelcome);
        };
    }, [socket]);
    // Handle username submission
    const handleUsernameSubmit = useCallback((e) => {
        e.preventDefault();
        const trimmedUsername = username.trim();
        if (trimmedUsername && socket) {
            console.log('ðŸ‘¤ Setting username to:', trimmedUsername);
            onUsernameSet(trimmedUsername);
            // Send username to server via chat command
            socket.emit('chat-command', {
                command: 'username',
                args: [trimmedUsername],
                username: trimmedUsername,
                socketId: socket.id
            });
            // Clear the username input
            setUsername('');
        }
    }, [username, socket, onUsernameSet]);
    // Handle message submission
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        const trimmedMessage = message.trim();
        if (!trimmedMessage || !socket) {
            return;
        }
        console.log('Sending message:', trimmedMessage);
        // Check if it's a command
        if (trimmedMessage.startsWith('/')) {
            // Commands can be sent even without username (for /login, /register)
            const parts = trimmedMessage.slice(1).split(' '); // Remove '/' and split
            const command = parts[0]; // First part is command name
            const args = parts.slice(1); // Rest are arguments
            socket.emit('chat-command', {
                command: command,
                args: args,
                username: currentUsername || '',
                socketId: socket.id
            });
        }
        else {
            // Regular messages require username (server will validate)
            if (!currentUsername) {
                return;
            }
            socket.emit('chat-message', {
                message: trimmedMessage,
                username: currentUsername,
                socketId: socket.id
            });
        }
        setMessage('');
    }, [message, socket, currentUsername]);
    // Handle /panel command click
    const handlePanelClick = useCallback(() => {
        if (socket) {
            console.log('ðŸŽ›ï¸ Opening admin panel via clickable link');
            socket.emit('chat-command', {
                command: 'panel',
                args: []
            });
        }
    }, [socket]);
    const toggleChat = useCallback(() => {
        console.log('Toggling chat from', chatOpen, 'to', !chatOpen, 'Messages count:', messages.length);
        onClose();
    }, [chatOpen, messages.length, onClose]);
    const toggleTheme = useCallback(() => {
        onThemeChange(!isDarkMode);
    }, [isDarkMode, onThemeChange]);
    const formatTime = useCallback((timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }, []);
    // Render message content with clickable /panel link
    const renderMessageContent = useCallback((msg, index) => {
        // Render clickable /panel link
        if (msg.hasAdminLink && msg.message.includes('/panel')) {
            const parts = msg.message.split('/panel');
            return (_jsxs(_Fragment, { children: [parts[0], _jsx("button", { onClick: handlePanelClick, style: {
                            background: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            marginLeft: '4px',
                            marginRight: '4px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease'
                        }, onMouseEnter: (e) => {
                            e.currentTarget.style.background = '#7c3aed';
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.background = '#8b5cf6';
                        }, children: "/panel" }), parts[1]] }));
        }
        return msg.message;
    }, [handlePanelClick, isDarkMode]);
    return (_jsxs("div", { className: `chat-container ${chatOpen ? '' : 'collapsed'} ${isDarkMode ? 'dark-mode' : 'light-mode'}`, children: [_jsxs("div", { className: "chat-header", children: [_jsx("button", { className: "chat-toggle", onClick: toggleChat, children: chatOpen ? '-' : '+' }), _jsx("div", { className: "chat-title", children: _jsx("span", { className: "seasonal-pumpkin", children: "\u00F0\u0178\u017D\u0192" }) }), _jsx("button", { className: "theme-toggle", onClick: toggleTheme, title: isDarkMode ? 'Switch to light mode' : 'Switch to dark mode', children: _jsx("div", { className: "switch-container", children: _jsx("div", { className: `switch ${isDarkMode ? 'dark' : 'light'}`, children: _jsx("div", { className: "switch-handle" }) }) }) })] }), _jsxs("div", { className: "chat-content", children: [_jsxs("div", { className: "chat-messages", children: [messages.map((msg, index) => (_jsxs("div", { className: `chat-message ${msg.type === 'system' ? 'system' : ''} ${msg.type === 'command' ? 'error' : ''}`, children: [msg.type !== 'system' && (_jsxs("div", { className: "message-header", children: [_jsx("span", { className: "message-username", children: msg.username || 'Anonymous' }), _jsx("span", { className: "message-time", children: formatTime(msg.timestamp) })] })), _jsx("div", { className: "message-content", children: renderMessageContent(msg, index) })] }, `${msg.timestamp}-${index}`))), _jsx("div", { ref: messagesEndRef })] }), _jsx("div", { className: "chat-input-area", children: isAuthenticated ? (_jsx("div", { className: "chat-input-container", children: _jsx("input", { type: "text", value: message, onChange: (e) => setMessage(e.target.value), placeholder: "", className: "chat-input", maxLength: MAX_MESSAGE_LENGTH, onKeyPress: (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                } }) })) : (_jsx("div", { className: "chat-input-container", children: _jsx("input", { type: "text", value: message, onChange: (e) => setMessage(e.target.value), placeholder: "Type /login or /register", className: "chat-input", maxLength: MAX_MESSAGE_LENGTH, onKeyPress: (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                } }) })) })] })] }));
};
export default ChatPanel;
